import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import logo from "../logo.svg";
import { LANGUAGES, SUPPORTED_LANGS } from "../i18n";
import ThemeToggle from "./ThemeToggle";
import "./Header.css";

function useLangPath() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  return (path) => (SUPPORTED_LANGS.includes(lang) ? `/${lang}${path}` : path);
}

const FOREX_ITEMS = [
  { label: "EUR/USD", path: "/forex-majors/eurusd-ai-trading-predictions" },
  { label: "GBP/USD", path: "/forex-majors/gbpusd-ai-trading-predictions" },
  { label: "USD/JPY", path: "/forex-majors/usdjpy-ai-trading-predictions" },
  { label: "USD/CAD", path: "/forex-majors/usdcad-ai-trading-predictions" },
  { label: "AUD/USD", path: "/forex-majors/audusd-ai-trading-predictions" },
];

const METALS_ITEMS = [
  { label: "Gold",   path: "/metals/gold-ai-trading-predictions" },
  { label: "Silver", path: "/metals/silver-ai-trading-predictions" },
  { label: "Copper", path: "/metals/copper-ai-trading-predictions" },
];

const INDICES_ITEMS = [
  { label: "NAS100", path: "/indices/nas100-ai-trading-predictions" },
  { label: "US500",  path: "/indices/us500-ai-trading-predictions" },
  { label: "US30",   path: "/indices/us30-ai-trading-predictions" },
  { label: "UK100",  path: "/indices/uk100-ai-trading-predictions" },
];

const CRYPTO_ITEMS = [
  { label: "Bitcoin",      path: "/cryptocurrencies/bitcoin-ai-trading-predictions" },
  { label: "Ethereum",     path: "/cryptocurrencies/ethereum-ai-trading-predictions" },
  { label: "Ripple",       path: "/cryptocurrencies/ripple-ai-trading-predictions" },
  { label: "Solana",       path: "/cryptocurrencies/solana-ai-trading-predictions" },
  { label: "Litecoin",     path: "/cryptocurrencies/litecoin-ai-trading-predictions" },
  { label: "Bitcoin Cash", path: "/cryptocurrencies/bitcoin-cash-ai-trading-predictions" },
];

const COMMODITIES_ITEMS = [
  { label: "Brent Oil",  path: "/commodities/brent-crude-oil-ai-trading-predictions" },
  { label: "Natural Gas",path: "/commodities/natural-gas-ai-trading-predictions" },
  { label: "Cocoa",      path: "/commodities/cocoa-ai-trading-predictions" },
  { label: "Sugar",      path: "/commodities/sugar-ai-trading-predictions" },
];

function HoverDropdown({ label, items, langPath, twoCol, onLinkClick }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="hd-item"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className="hd-label"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label} <span className="hd-caret">▾</span>
      </button>
      {open && (
        <div className={`hd-menu${twoCol ? " hd-menu--two-col" : ""}`}>
          {items.map((item) => (
            <Link
              key={item.path}
              to={langPath(item.path)}
              className="hd-menu-item"
              onClick={() => { setOpen(false); onLinkClick && onLinkClick(); }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function LangDropdown({ currentLang, onSwitch }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="hd-item"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className="hd-label"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {currentLang.flag} {currentLang.name} <span className="hd-caret">▾</span>
      </button>
      {open && (
        <div className="hd-menu hd-menu--right">
          {LANGUAGES.map(({ code, name, flag }) => (
            <button
              key={code}
              className="hd-menu-item hd-menu-btn"
              onClick={() => { onSwitch(code); setOpen(false); }}
            >
              {flag} {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const langPath = useLangPath();
  const [mobileOpen, setMobileOpen] = useState(false);

  function switchLanguage(code) {
    const currentLang = i18n.language;
    let currentPath = location.pathname;
    if (SUPPORTED_LANGS.includes(currentLang)) {
      currentPath = currentPath.replace(new RegExp(`^/${currentLang}`), "") || "/";
    }
    if (code === "en") {
      i18n.changeLanguage("en");
      Cookies.remove("lang");
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
      navigate(currentPath || "/");
    } else {
      i18n.changeLanguage(code);
      Cookies.set("lang", code, { expires: 365 });
      document.documentElement.lang = code;
      document.documentElement.dir = code === "ar" ? "rtl" : "ltr";
      navigate(currentPath === "/" ? `/${code}` : `/${code}${currentPath}`);
    }
    setMobileOpen(false);
  }

  const currentLangObj = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  return (
    <header className="App-header">
      <div className="hd-bar">
        <Link to={langPath("/")} className="hd-brand">
          <img src={logo} className="App-logo" alt="A.I. Trading Predictions Logo" height="40" />
        </Link>

        <button
          className="hd-toggler"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle navigation"
          aria-expanded={mobileOpen}
        >
          <span /><span /><span />
        </button>

        <nav className={`hd-nav${mobileOpen ? " hd-nav--open" : ""}`}>
          <div className="hd-nav-left">
            <HoverDropdown label="Forex"                    items={FOREX_ITEMS}       langPath={langPath}        onLinkClick={() => setMobileOpen(false)} />
            <HoverDropdown label={t("nav.metals")}          items={METALS_ITEMS}      langPath={langPath}        onLinkClick={() => setMobileOpen(false)} />
            <HoverDropdown label={t("nav.indices")}         items={INDICES_ITEMS}     langPath={langPath}        onLinkClick={() => setMobileOpen(false)} />
            <HoverDropdown label={t("nav.cryptocurrencies")}items={CRYPTO_ITEMS}      langPath={langPath}        onLinkClick={() => setMobileOpen(false)} />
            <HoverDropdown label={t("nav.commodities")}     items={COMMODITIES_ITEMS} langPath={langPath}        onLinkClick={() => setMobileOpen(false)} />
            <Link to={langPath("/articles")}  className="hd-link" onClick={() => setMobileOpen(false)}>{t("nav.articles")}</Link>
            <Link to={langPath("/contact-us")} className="hd-link" onClick={() => setMobileOpen(false)}>{t("nav.contactUs")}</Link>
          </div>
          <div className="hd-nav-right">
            <ThemeToggle />
            <LangDropdown currentLang={currentLangObj} onSwitch={switchLanguage} />
          </div>
        </nav>
      </div>
    </header>
  );
}

export default Header;
